import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Users, CalendarDays, DollarSign, ChevronLeft, ChevronRight,
  Download, Edit3, Check, X, Plus, Trash2, Waves, Clock
} from 'lucide-react';
import { UnassignedSessions } from '@/components/UnassignedSessions';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function FacilityDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('daily');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [editRates, setEditRates] = useState(false);
  const [rates, setRates] = useState([]);
  const [newCat, setNewCat] = useState({ category: '', price: '' });
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ facility_name: '', email: '' });
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
  const [historyMonth, setHistoryMonth] = useState('');
  const [yearlyData, setYearlyData] = useState(null);
  const [monthReport, setMonthReport] = useState(null);
  const [reportRecipients, setReportRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [allFacilities, setAllFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  // Fetch all facilities for admin switcher
  useEffect(() => {
    if (isAdmin) {
      api.get('/facility/accounts').then(r => {
        setAllFacilities(r.data);
        if (r.data.length > 0 && !selectedFacilityId) setSelectedFacilityId(r.data[0].facility_id);
      }).catch(() => {});
    }
  }, [isAdmin, selectedFacilityId]);

  const fetchData = useCallback(async () => {
    try {
      const params = selectedFacilityId ? `?facility_id=${selectedFacilityId}` : '';
      const res = await api.get(`/facility/dashboard${params}`);
      setData(res.data);
      if (res.data.rates) setRates(res.data.rates);
      if (res.data.facility?.facility_id) {
        const rRes = await api.get(`/facility/${res.data.facility.facility_id}/report-recipients`);
        setReportRecipients(rRes.data.recipients || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [selectedFacilityId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchYearlySummary = useCallback(async (year) => {
    if (!data?.facility) {
      // Show empty year structure even without facility
      setYearlyData({ year: String(year), by_month: {}, grand_total_entries: 0, grand_total_cost: 0 });
      return;
    }
    try {
      const res = await api.get(`/facility/yearly-summary?facility_id=${data.facility.facility_id}&year=${year}`);
      setYearlyData(res.data);
    } catch { setYearlyData({ year: String(year), by_month: {}, grand_total_entries: 0, grand_total_cost: 0 }); }
  }, [data?.facility]);

  const fetchMonthReport = useCallback(async (month) => {
    if (!data?.facility) return;
    try {
      const res = await api.get(`/facility/monthly-report?facility_id=${data.facility.facility_id}&month=${month}`);
      setMonthReport(res.data);
    } catch { /* silent */ }
  }, [data?.facility]);

  useEffect(() => {
    if (view === 'history' && data?.facility) {
      fetchYearlySummary(historyYear);
      if (historyMonth) fetchMonthReport(`${historyYear}-${String(historyMonth).padStart(2, '0')}`);
    }
  }, [view, historyYear, historyMonth, data?.facility, fetchYearlySummary, fetchMonthReport]);

  const exportMonthCSV = async (month) => {
    if (!data?.facility) return;
    try {
      const res = await api.get(`/facility/monthly-report/export-csv?facility_id=${data.facility.facility_id}&month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `admissions_${month}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch { toast.error('Export failed'); }
  };

  const exportYearlyCSV = async () => {
    if (!data?.facility) return;
    try {
      const res = await api.get(`/facility/yearly-report/export-csv?facility_id=${data.facility.facility_id}&year=${historyYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `admissions_${historyYear}_yearly.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Yearly report downloaded');
    } catch { toast.error('Export failed'); }
  };

  const addRecipient = async () => {
    const email = newRecipient.trim();
    if (!email || !email.includes('@') || !data?.facility) return;
    const updated = [...reportRecipients, email];
    try {
      await api.put(`/facility/${data.facility.facility_id}/report-recipients`, { recipients: updated });
      setReportRecipients(updated);
      setNewRecipient('');
      toast.success('Recipient added');
    } catch { toast.error('Failed to add recipient'); }
  };

  const removeRecipient = async (email) => {
    if (!data?.facility) return;
    const updated = reportRecipients.filter(r => r !== email);
    try {
      await api.put(`/facility/${data.facility.facility_id}/report-recipients`, { recipients: updated });
      setReportRecipients(updated);
      toast.success('Recipient removed');
    } catch { toast.error('Failed to remove'); }
  };

  const saveRates = async () => {
    if (!data?.facility) return;
    try {
      await api.put('/facility/rates', {
        facility_id: data.facility.facility_id,
        rates: rates.map(r => ({ category: r.category, price: r.price, is_donated: r.is_donated || false })),
      });
      toast.success('Admission rates saved');
      setEditRates(false);
      fetchData();
    } catch { toast.error('Failed to save rates'); }
  };

  const addCategory = () => {
    if (!newCat.category) return;
    setRates(prev => [...prev, { category: newCat.category, price: parseFloat(newCat.price) || 0, is_donated: false }]);
    setNewCat({ category: '', price: '' });
  };

  const sendInvite = async () => {
    try {
      await api.post('/admin/facility/invite', inviteForm);
      toast.success('Invite sent!');
      setShowInvite(false);
      setInviteForm({ facility_name: '', email: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  // Admission summaries
  const admissions = data?.admissions || [];
  const dailyMap = useMemo(() => {
    const m = {};
    for (const a of admissions) {
      const d = a.date;
      if (!m[d]) m[d] = { entries: 0, cost: 0, records: [] };
      m[d].entries += (a.entries || []).length;
      m[d].cost += a.total || 0;
      m[d].records.push(a);
    }
    return m;
  }, [admissions]);

  // Sessions map for calendar (upcoming + past)
  const sessionsMap = useMemo(() => {
    const m = {};
    for (const s of [...(data?.upcoming || []), ...(data?.past_sessions || [])]) {
      const d = s.date;
      if (!m[d]) m[d] = [];
      m[d].push(s);
    }
    return m;
  }, [data?.upcoming, data?.past_sessions]);

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekStartStr = weekStart.toISOString().slice(0, 10);
  const weekEndStr = new Date(weekStart.getTime() + 6 * 86400000).toISOString().slice(0, 10);

  const weeklyAdmissions = useMemo(() =>
    admissions.filter(a => a.date >= weekStartStr && a.date <= weekEndStr),
  [admissions, weekStartStr, weekEndStr]);

  const monthStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}`;
  const monthlyAdmissions = useMemo(() =>
    admissions.filter(a => a.date?.startsWith(monthStr)),
  [admissions, monthStr]);

  const totalEntries = admissions.reduce((s, a) => s + (a.entries || []).length, 0);
  const totalCost = admissions.reduce((s, a) => s + (a.total || 0), 0);

  // Calendar grid
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  if (loading) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-8 animate-fadeIn" data-testid="facility-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>
            <Building2 className="w-6 h-6 inline mr-2 text-[#22D3EE]" />
            {data?.facility?.facility_name || 'Aquatic Facility'}
          </h1>
          <p className="text-sm text-slate-500">Pool Admission Tracking & Management</p>
        </div>
        {/* Facility switcher for admin */}
        {isAdmin && allFacilities.length > 1 && (
          <div className="flex items-center gap-2" data-testid="facility-switcher">
            <select
              value={selectedFacilityId || ''}
              onChange={e => { setSelectedFacilityId(e.target.value); setLoading(true); }}
              className="rounded-xl border border-[#22D3EE] bg-white px-3 py-2 text-sm font-semibold text-[#0E7490] focus:ring-2 focus:ring-[#22D3EE]/30 outline-none"
              data-testid="facility-select"
            >
              {allFacilities.map(f => (
                <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-slate-800">{totalEntries}</p>
          <p className="text-xs text-slate-500">Total Entries</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-[#22D3EE]/30"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-[#0E7490]">${totalCost.toFixed(2)}</p>
          <p className="text-xs text-[#0E7490]">Total Cost</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-slate-800">{weeklyAdmissions.reduce((s, a) => s + (a.entries || []).length, 0)}</p>
          <p className="text-xs text-slate-500">This Week</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-slate-100"><CardContent className="p-4 text-center">
          <p className="text-2xl font-extrabold text-slate-800">{monthlyAdmissions.reduce((s, a) => s + (a.entries || []).length, 0)}</p>
          <p className="text-xs text-slate-500">This Month</p>
        </CardContent></Card>
      </div>

      {/* Unassigned sessions (admin only) */}
      {isAdmin && <UnassignedSessions facilities={allFacilities.length > 0 ? allFacilities : (data?.facility ? [data.facility] : [])} onAdmissionCreated={fetchData} />}

      {/* View Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
        {['daily', 'weekly', 'history', 'calendar', 'upcoming', 'rates'].map(v => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-2 rounded-xl text-sm font-bold capitalize whitespace-nowrap transition-all ${view === v ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`facility-view-${v}`}>{v}</button>
        ))}
      </div>

      {/* Daily View */}
      {view === 'daily' && (
        <div className="space-y-3">
          <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>Daily Admissions</h3>
          {Object.entries(dailyMap).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 30).map(([date, info]) => (
            <Card key={date} className="rounded-2xl border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-bold text-slate-800">{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                    <p className="text-xs text-slate-500">{info.entries} entries · ${info.cost.toFixed(2)}</p>
                  </div>
                  <Badge className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30">{info.entries} people</Badge>
                </div>
                {info.records.map(r => (
                  <div key={r.admission_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3 mb-2" data-testid={`admission-${r.admission_id}`}>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">{r.child_name} — {r.instructor_name}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {(r.entries || []).map((e, i) => (
                          <Badge key={i} variant="outline" className="rounded-full text-[10px]">{e.name} ({e.category}) ${e.price.toFixed(2)}</Badge>
                        ))}
                      </div>
                    </div>
                    {!r.parent_in_water && <Badge className="rounded-full bg-amber-50 text-amber-600 border-amber-200 text-[10px]">No parent in water</Badge>}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
          {Object.keys(dailyMap).length === 0 && <p className="text-center text-slate-400 py-8">No admissions recorded yet</p>}
        </div>
      )}

      {/* Weekly View */}
      {view === 'weekly' && (
        <div className="space-y-3">
          <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>This Week ({weekStartStr} — {weekEndStr})</h3>
          {weeklyAdmissions.length > 0 ? weeklyAdmissions.map(r => (
            <div key={r.admission_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
              <div>
                <p className="font-semibold text-slate-700 text-sm">{r.child_name} — {r.instructor_name}</p>
                <p className="text-xs text-slate-400">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {(r.entries || []).map((e, i) => (
                    <Badge key={i} variant="outline" className="rounded-full text-[10px]">{e.name} ({e.category})</Badge>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-[#0E7490]">${r.total?.toFixed(2)}</p>
                {!r.parent_in_water && <p className="text-[10px] text-amber-500">No parent</p>}
              </div>
            </div>
          )) : <p className="text-center text-slate-400 py-8">No admissions this week</p>}
          <div className="bg-[#ECFEFF] rounded-2xl p-4 text-center">
            <p className="text-xl font-extrabold text-[#0E7490]">{weeklyAdmissions.reduce((s, a) => s + (a.entries || []).length, 0)} entries · ${weeklyAdmissions.reduce((s, a) => s + (a.total || 0), 0).toFixed(2)}</p>
            <p className="text-xs text-[#0E7490]">Weekly Total</p>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}><ChevronLeft className="w-4 h-4" /></Button>
              <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>{MONTHS[calMonth]} {calYear}</h3>
              <Button variant="ghost" size="icon" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 py-1">{d}</div>)}
              {calCells.map((day, i) => {
                if (!day) return <div key={`e-${i}`} />;
                const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayData = dailyMap[dateStr];
                const daySessions = sessionsMap[dateStr] || [];
                const isToday = dateStr === todayStr;
                const hasData = dayData || daySessions.length > 0;
                return (
                  <div key={dateStr} className={`min-h-[56px] rounded-xl p-1 text-xs border transition-colors ${isToday ? 'border-[#22D3EE] bg-[#ECFEFF]' : dayData ? 'border-[#C4F017]/50 bg-[#F7FEE7]' : daySessions.length > 0 ? 'border-blue-200 bg-blue-50/50' : 'border-slate-100'}`}>
                    <span className={`font-bold ${isToday ? 'text-[#0E7490]' : 'text-slate-600'}`}>{day}</span>
                    {dayData && (
                      <div className="mt-0.5">
                        <div className="bg-[#1E1B4B] text-white text-[9px] rounded px-1 py-0.5 text-center font-bold">{dayData.entries} entry</div>
                        {dayData.cost > 0 && <div className="text-[9px] text-[#0E7490] text-center font-bold">${dayData.cost.toFixed(0)}</div>}
                      </div>
                    )}
                    {!dayData && daySessions.length > 0 && (
                      <div className="mt-0.5">
                        <div className="bg-blue-500 text-white text-[9px] rounded px-1 py-0.5 text-center font-bold">{daySessions.length} sess</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Sessions */}
      {view === 'upcoming' && (
        <div className="space-y-3">
          <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>Upcoming Sessions</h3>
          {(data?.upcoming || []).map((s, i) => (
            <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#ECFEFF] flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-[#22D3EE]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-700 text-sm">{s.child_name}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(s.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {s.time_slot ? ` · ${s.time_slot}` : ''}
                  </p>
                  <div className="flex gap-3 mt-1 text-[11px]">
                    {s.instructor_name && <span className="text-[#0E7490] font-semibold">Instructor: {s.instructor_name}</span>}
                    {s.parent_name && <span className="text-slate-400">Parent: {s.parent_name}</span>}
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="rounded-full text-xs capitalize">{s.schedule_type === 'meet_and_greet' ? 'Meet & Greet' : s.schedule_type}</Badge>
            </div>
          ))}
          {(data?.upcoming || []).length === 0 && <p className="text-center text-slate-400 py-8">No upcoming sessions</p>}
        </div>
      )}


      {/* History View - Monthly/Yearly breakdown */}
      {view === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>Admission History</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => { setHistoryYear(y => y - 1); setHistoryMonth(''); }}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="font-bold text-slate-800 text-lg min-w-[60px] text-center">{historyYear}</span>
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => { setHistoryYear(y => y + 1); setHistoryMonth(''); }}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Yearly summary */}
          {yearlyData && (
            <Card className="rounded-2xl border-[#22D3EE]/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-2xl font-extrabold text-[#0E7490]">{yearlyData.grand_total_entries} entries</p>
                    <p className="text-sm text-slate-500">${yearlyData.grand_total_cost.toFixed(2)} total for {historyYear}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {historyMonth && (
                      <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setHistoryMonth('')}>View All Months</Button>
                    )}
                    {!historyMonth && yearlyData.grand_total_entries > 0 && (
                      <Button variant="outline" size="sm" className="rounded-full gap-1 text-[#0E7490] border-[#22D3EE]" onClick={exportYearlyCSV} data-testid="export-yearly-csv">
                        <Download className="w-4 h-4" /> Yearly CSV
                      </Button>
                    )}
                  </div>
                </div>

                {!historyMonth && (
                  <div className="space-y-2">
                    {MONTHS.map((m, i) => {
                      const key = `${historyYear}-${String(i + 1).padStart(2, '0')}`;
                      const monthData = yearlyData.by_month[key];
                      return (
                        <button
                          key={key}
                          onClick={() => setHistoryMonth(i + 1)}
                          className={`w-full flex items-center justify-between rounded-xl p-3 transition-colors ${monthData ? 'bg-[#F7FEE7] hover:bg-[#ECFCCB]' : 'bg-slate-50 hover:bg-slate-100'}`}
                          data-testid={`history-month-${key}`}
                        >
                          <span className="font-semibold text-slate-700 text-sm">{m} {historyYear}</span>
                          {monthData ? (
                            <div className="flex items-center gap-3">
                              <Badge className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30 text-xs">{monthData.entries} entries</Badge>
                              <span className="font-bold text-[#0E7490] text-sm">${monthData.cost.toFixed(2)}</span>
                              <Download className="w-4 h-4 text-slate-400 hover:text-[#0E7490]" onClick={(e) => { e.stopPropagation(); exportMonthCSV(key); }} />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No admissions</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Month detail */}
                {historyMonth && monthReport && (
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-700">{MONTHS[historyMonth - 1]} {historyYear} Detail</h4>
                      <Button variant="outline" size="sm" className="rounded-full gap-1 text-[#0E7490] border-[#22D3EE]" onClick={() => exportMonthCSV(`${historyYear}-${String(historyMonth).padStart(2, '0')}`)} data-testid="export-month-csv">
                        <Download className="w-4 h-4" /> Export CSV
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-extrabold text-slate-800">{monthReport.total_entries}</p>
                        <p className="text-xs text-slate-500">Entries</p>
                      </div>
                      <div className="bg-[#ECFEFF] rounded-xl p-3 text-center">
                        <p className="text-lg font-extrabold text-[#0E7490]">${monthReport.total_cost.toFixed(2)}</p>
                        <p className="text-xs text-[#0E7490]">Cost</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-lg font-extrabold text-slate-800">{(monthReport.admissions || []).length}</p>
                        <p className="text-xs text-slate-500">Sessions</p>
                      </div>
                    </div>
                    {/* Category breakdown */}
                    {Object.keys(monthReport.by_category || {}).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {Object.entries(monthReport.by_category).map(([cat, info]) => (
                          <Badge key={cat} variant="outline" className="rounded-full text-xs">{cat}: {info.count} × ${info.cost.toFixed(2)}</Badge>
                        ))}
                      </div>
                    )}
                    {/* Individual admissions */}
                    {(monthReport.admissions || []).map(r => (
                      <div key={r.admission_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                        <div>
                          <p className="font-semibold text-slate-700 text-sm">{r.child_name} — {r.instructor_name}</p>
                          <p className="text-xs text-slate-400">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {(r.entries || []).map((e, i) => (
                              <Badge key={i} variant="outline" className="rounded-full text-[10px]">{e.name} ({e.category})</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#0E7490]">${r.total?.toFixed(2)}</p>
                          {!r.parent_in_water && <p className="text-[10px] text-amber-500">No parent</p>}
                        </div>
                      </div>
                    ))}
                    {(monthReport.admissions || []).length === 0 && <p className="text-center text-slate-400 py-4">No admissions this month</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Past Sessions */}


      {/* Rates Management */}
      {view === 'rates' && (
        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Nunito' }}>Admission Rates</CardTitle>
            <Button onClick={() => editRates ? saveRates() : setEditRates(true)} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" data-testid="edit-rates-btn">
              {editRates ? <><Check className="w-4 h-4" /> Save</> : <><Edit3 className="w-4 h-4" /> Edit Rates</>}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {rates.map((r, i) => (
              <div key={r.rate_id || i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3" data-testid={`rate-${r.category}`}>
                <div className="flex-1">
                  <p className="font-semibold text-slate-700 text-sm">{r.label || r.category}</p>
                </div>
                {editRates ? (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">$</span>
                    <Input type="number" step="0.01" className="w-24 rounded-xl h-9 text-sm" value={r.price} onChange={e => setRates(prev => prev.map((x, j) => j === i ? {...x, price: parseFloat(e.target.value) || 0} : x))} />
                    <button onClick={() => setRates(prev => prev.map((x, j) => j === i ? {...x, is_donated: !x.is_donated} : x))} className={`px-3 py-1 rounded-full text-xs font-bold ${r.is_donated ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>{r.is_donated ? 'Donated' : 'Paid'}</button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => setRates(prev => prev.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <div className="text-right">
                    <p className="font-bold text-[#0E7490]">${r.price?.toFixed(2)}</p>
                    {r.is_donated && <Badge className="rounded-full bg-green-50 text-green-600 border-green-200 text-[10px]">Donated</Badge>}
                  </div>
                )}
              </div>
            ))}
            {editRates && (
              <div className="flex items-center gap-2 mt-3 border-t border-slate-100 pt-3">
                <Input className="rounded-xl h-9 text-sm flex-1" placeholder="Category name" value={newCat.category} onChange={e => setNewCat(c => ({...c, category: e.target.value}))} />
                <Input type="number" step="0.01" className="rounded-xl h-9 text-sm w-24" placeholder="Price" value={newCat.price} onChange={e => setNewCat(c => ({...c, price: e.target.value}))} />
                <Button size="sm" onClick={addCategory} className="rounded-xl bg-[#22D3EE] h-9 gap-1"><Plus className="w-4 h-4" /> Add</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Report Recipients Management */}
      {data?.facility && (
        <Card className="rounded-2xl border-slate-100 mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Nunito' }} className="text-base">Monthly Report Recipients</CardTitle>
              <p className="text-xs text-slate-500 mt-1">These emails receive the auto-generated monthly admission report</p>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {reportRecipients.length > 0 ? reportRecipients.map(email => (
              <div key={email} className="flex items-center justify-between bg-slate-50 rounded-xl p-3" data-testid={`recipient-${email}`}>
                <span className="text-sm text-slate-700 font-medium">{email}</span>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => removeRecipient(email)} data-testid={`remove-recipient-${email}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )) : (
              <p className="text-sm text-slate-400 py-2">No additional recipients. Only the facility manager receives reports.</p>
            )}
            <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
              <Input
                type="email"
                className="rounded-xl h-9 text-sm flex-1"
                placeholder="accountant@example.com"
                value={newRecipient}
                onChange={e => setNewRecipient(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRecipient()}
                data-testid="new-recipient-input"
              />
              <Button size="sm" onClick={addRecipient} className="rounded-xl bg-[#22D3EE] hover:bg-[#06B6D4] h-9 gap-1" data-testid="add-recipient-btn">
                <Plus className="w-4 h-4" /> Add
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Facility Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>Invite Aquatic Facility</DialogTitle>
            <DialogDescription className="sr-only">Send an invitation to a facility manager</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-semibold text-slate-600">Facility Name</label>
              <Input className="rounded-xl mt-1" placeholder="e.g., Grand Forks Aquatic Centre" value={inviteForm.facility_name} onChange={e => setInviteForm(f => ({...f, facility_name: e.target.value}))} data-testid="invite-facility-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Manager Email</label>
              <Input type="email" className="rounded-xl mt-1" placeholder="manager@aquaticcentre.ca" value={inviteForm.email} onChange={e => setInviteForm(f => ({...f, email: e.target.value}))} data-testid="invite-facility-email" />
            </div>
            <Button onClick={sendInvite} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11" data-testid="send-facility-invite">Send Invitation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
